import { formatClockMinutesTo12Hour } from '../lib/timeline';
import {
  OVERVIEW_EVENT_TYPE,
  type OverviewEvent,
  type RoomRowData,
} from '../lib/types';
import type {
  RoomOverviewCardField,
  RoomOverviewEventCardData,
} from './room-overview-mobile-cards.types';

export function hasRoomOverviewFieldValue(value: string | undefined): boolean {
  try {
    return Boolean(value?.trim());
  } catch (error) {
    console.error('Failed to check room overview field value', { value, error });
    throw error;
  }
}

export function getRoomOverviewCardEvents(
  row: RoomRowData,
): Array<OverviewEvent> {
  try {
    const events: Array<OverviewEvent> = [];
    for (const lane of row.lanes) {
      for (const positioned of lane) {
        events.push(positioned.event);
      }
    }

    return events.sort((left, right) => left.startMinutes - right.startMinutes);
  } catch (error) {
    console.error('Failed to collect room overview card events', {
      roomId: row.roomId,
      error,
    });
    throw error;
  }
}

export function getRoomOverviewEventCardFields(
  event: OverviewEvent,
): Array<RoomOverviewCardField> {
  try {
    const fields: Array<RoomOverviewCardField> = [
      {
        label: 'Time',
        value: `${formatClockMinutesTo12Hour(event.startMinutes)} - ${formatClockMinutesTo12Hour(event.endMinutes)}`,
      },
    ];

    if (hasRoomOverviewFieldValue(event.instructorName)) {
      fields.push({
        label: 'Instructor',
        value: event.instructorName.trim(),
      });
    }

    if (hasRoomOverviewFieldValue(event.studentName)) {
      fields.push({
        label: 'Student',
        value: event.studentName.trim(),
      });
    }

    if (hasRoomOverviewFieldValue(event.locationName)) {
      fields.push({
        label: 'Location',
        value: event.locationName.trim(),
      });
    }

    return fields;
  } catch (error) {
    console.error('Failed to build room overview event card fields', {
      classId: event.classId,
      error,
    });
    throw error;
  }
}

export function getRoomOverviewEventCards(
  row: RoomRowData,
): Array<RoomOverviewEventCardData> {
  try {
    return getRoomOverviewCardEvents(row).map((event) => ({
      event,
      fields: getRoomOverviewEventCardFields(event),
    }));
  } catch (error) {
    console.error('Failed to build room overview event cards', {
      roomId: row.roomId,
      error,
    });
    throw error;
  }
}

export function isPrivateLessonOverviewEvent(event: OverviewEvent): boolean {
  try {
    return event.type === OVERVIEW_EVENT_TYPE.PRIVATE_LESSON;
  } catch (error) {
    console.error('Failed to classify room overview event type', {
      classId: event.classId,
      error,
    });
    throw error;
  }
}
